import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './styles.css';
import { registerServiceWorker } from './updates.ts';

const container = document.getElementById('root');
if (!container) throw new Error('Root element is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/**
 * Publish the on-screen keyboard's height as `--keyboard-inset`.
 *
 * iOS does not shrink the layout viewport when the keyboard opens — it shrinks
 * the *visual* viewport and leaves `position: fixed` alone. A bottom-anchored
 * sheet therefore stays pinned to the bottom of the screen, underneath the
 * keyboard, taking its Save button and its lower fields with it. The only way
 * to know how much room was taken is to measure the visual viewport.
 *
 * Everything anchored to the bottom of the screen subtracts this.
 */
function trackKeyboardInset(): void {
  const vv = window.visualViewport;
  if (!vv) return;

  const root = document.documentElement;
  let raf = 0;

  const update = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      // What the keyboard covers: the gap between the layout viewport's bottom
      // and the visible one's. Clamped at zero because scroll bounce briefly
      // makes this negative.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--keyboard-inset', `${Math.round(inset)}px`);
      // A threshold rather than "> 0": the URL bar collapsing also shrinks the
      // visual viewport, by far less than any keyboard.
      root.classList.toggle('keyboard-open', inset > 120);
    });
  };

  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
}

trackKeyboardInset();

// Offline shell. Registered after load so it never competes with the first
// render, and only in a build — during development a service worker serving a
// cached bundle is a debugging trap, not a feature.
//
// Registration also watches for a newer build taking over, which it did not
// before: a Home Screen install is suspended and restored rather than
// reloaded, so it would happily run a months-old bundle against a current
// server and give no sign that a deploy had ever happened.
if (import.meta.env.PROD) {
  window.addEventListener('load', registerServiceWorker);
}
